import { assign, createMachine, InterpreterFrom, StateMachine, StateValueFrom } from "xstate"
import * as O from "fp-ts/Option"
import * as F from "fp-ts/function"
import {
    BookingData,
    BookingFormTagged,
    CourtesyHoldData,
    CourtesyHoldFormTagged,
    FormData,
    mapToBooking,
    mapToCourtesyHold,
    mapToWebQuote,
    WebQuoteData,
    WebQuoteFormTagged,
} from "../../guests-info/form-data"
import { assertEvent } from "../../../_common/xstate-helpers"

import { Ev } from "../../../_common/machines/event"

export const switchFlow = (state: SwitchFlowState): SwitchFlowStateValue => {
    return state.value as SwitchFlowStateValue
}

export type CourtesyHoldTrigger = "in-page-form"

type SwitchFlowContext = {
    private: {
        rawFormData: O.Option<FormData>
        rawWebQuoteFormData: O.Option<WebQuoteFormTagged>
        rawBookingFormData: O.Option<BookingFormTagged>
        rawCourtesyHoldFormData: O.Option<CourtesyHoldFormTagged>
    }

    courtesyHoldTrigger: O.Option<CourtesyHoldTrigger>
    bookingData: O.Option<BookingData>
    webQuoteData: O.Option<WebQuoteData>
    courtesyHoldData: O.Option<CourtesyHoldData>
}
type Typestates = { value: "booking" | "webQuote" | "courtesyHold"; context: SwitchFlowContext }
type SwitchFlowEvents =
    | Ev<"SELECT_WEB_QUOTE_FLOW">
    | Ev<"SELECT_BOOKING_FLOW">
    | Ev<"SELECT_COURTESY_HOLD_FLOW", { trigger: CourtesyHoldTrigger }>
    | Ev<"UPDATE_BOOKING_FORM", { data: BookingData }>
    | Ev<"UPDATE_WEB_QUOTE_FORM", { data: WebQuoteData }>
    | Ev<"UPDATE_COURTESY_HOLD_FORM", { data: CourtesyHoldData }>
    | Ev<"MAP_BOOKING_OR_WEB_QUOTE">
    | Ev<"MAP_BOOKING_OR_COURTESY_HOLD">

export const switchFlowMachine = createMachine<SwitchFlowContext, SwitchFlowEvents, Typestates>(
    {
        id: "switch-flow",
        initial: "booking",
        predictableActionArguments: true,
        context: {
            private: {
                rawFormData: O.none,
                rawWebQuoteFormData: O.none,
                rawBookingFormData: O.none,
                rawCourtesyHoldFormData: O.none,
            },
            webQuoteData: O.none,
            bookingData: O.none,
            courtesyHoldData: O.none,
            courtesyHoldTrigger: O.none,
        },
        states: {
            booking: {
                entry: ["mapToBooking"],
                on: {
                    SELECT_WEB_QUOTE_FLOW: { target: "webQuote", actions: ["copyWebQuote"] },
                    SELECT_COURTESY_HOLD_FLOW: { target: "courtesyHold", actions: ["setCourtesyHoldTrigger"] },
                    UPDATE_BOOKING_FORM: { actions: ["updateBooking"] },
                    MAP_BOOKING_OR_WEB_QUOTE: { actions: ["mapToBooking"] },
                    MAP_BOOKING_OR_COURTESY_HOLD: { actions: ["mapToBooking"] },
                },
            },
            webQuote: {
                entry: ["mapToWebQuote"],
                on: {
                    SELECT_BOOKING_FLOW: { target: "booking" },
                    UPDATE_WEB_QUOTE_FORM: { actions: ["updateWebQuote"] },
                    MAP_BOOKING_OR_WEB_QUOTE: { actions: ["mapToWebQuote"] },
                },
            },
            courtesyHold: {
                entry: ["mapToCourtesyHold"],
                on: {
                    SELECT_BOOKING_FLOW: { target: "booking" },
                    UPDATE_COURTESY_HOLD_FORM: { actions: ["updateCourtesyHold"] },
                    MAP_BOOKING_OR_COURTESY_HOLD: { actions: ["mapToCourtesyHold"] },
                },
            },
        },
    },
    {
        actions: {
            setCourtesyHoldTrigger: assign({
                courtesyHoldTrigger: (_, ev) => {
                    // return O.some("exit-strategy")
                    assertEvent(ev, "SELECT_COURTESY_HOLD_FLOW")
                    return O.some(ev.trigger)
                },
            }),
            mapToWebQuote: assign({
                webQuoteData: (ctx) => mapToWebQuote(ctx.private.rawFormData, ctx.private.rawWebQuoteFormData),
            }),
            mapToBooking: assign({
                bookingData: (ctx) => mapToBooking(ctx.private.rawFormData, ctx.private.rawBookingFormData),
            }),
            mapToCourtesyHold: assign({
                courtesyHoldData: (ctx) =>
                    mapToCourtesyHold(ctx.private.rawFormData, ctx.private.rawCourtesyHoldFormData),
            }),
            updateBooking: assign((ctx, ev) => {
                assertEvent(ev, "UPDATE_BOOKING_FORM")
                const updatedData = O.some({ ...ev.data, type: "booking" as const })
                return {
                    private: {
                        ...ctx.private,
                        rawFormData: updatedData,
                        rawBookingFormData: updatedData,
                    },
                }
            }),
            updateWebQuote: assign((ctx, ev) => {
                assertEvent(ev, "UPDATE_WEB_QUOTE_FORM")
                const mapped = O.some({ ...ev.data, type: "web-quote" as const })
                return updateWebQuoteRawForm(ctx.private, mapped)
            }),
            updateCourtesyHold: assign((ctx, ev) => {
                assertEvent(ev, "UPDATE_COURTESY_HOLD_FORM")
                const updatedData = O.some({ ...ev.data, type: "courtesyHold" as const })
                return {
                    private: {
                        ...ctx.private,
                        rawFormData: updatedData,
                        rawCourtesyHoldFormData: updatedData,
                    },
                }
            }),
            // NOTE: this is an hack to keep the phone number between the switch booking -> webQuote
            // Somewhere the phone number value is lost, and set to undefined. We couldn't figure out where exactly.
            copyWebQuote: assign((ctx, ev) => {
                assertEvent(ev, "SELECT_WEB_QUOTE_FLOW")
                const mapped = F.pipe(
                    mapToWebQuote(ctx.private.rawFormData, ctx.private.rawWebQuoteFormData),
                    O.map((x) => ({ type: "web-quote" as const, ...x })),
                )
                return updateWebQuoteRawForm(ctx.private, mapped)
            }),
        },
    },
)

const updateWebQuoteRawForm = (
    rawForms: SwitchFlowContext["private"],
    newWebQuote: O.Option<WebQuoteFormTagged>,
): Partial<SwitchFlowContext> => ({
    private: {
        ...rawForms,
        rawFormData: newWebQuote,
        rawWebQuoteFormData: newWebQuote,
    },
})

export type SwitchFlowMachine = StateMachine<
    SwitchFlowContext,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    SwitchFlowEvents,
    Typestates
>
export type SwitchFlowService = InterpreterFrom<SwitchFlowMachine>
export type SwitchFlowState = SwitchFlowService["state"]
export type SwitchFlowSend = SwitchFlowService["send"]
export type SwitchFlowStateValue = StateValueFrom<SwitchFlowMachine>
